import { useNavigate } from 'react-router-dom';
import { Calendar, User, Link2, Trash2, Pencil, FileEdit, Target, Megaphone, BookOpen, UsersRound, Settings, Search } from 'lucide-react';
import { Tooltip } from 'antd';
import type { FC } from 'react';

export interface Milestone {
  id: string;
  title: string;
  description?: string | null;
  status: string;
  category: string;
  dueDate?: string | null;
  completedAt?: string | null;
  sortOrder: number;
  linkedModule?: string | null;
  linkedEntityId?: string | null;
  assignee?: string | null;
  createdAt: string;
}

const CATEGORY_COLORS: Record<string, string> = {
  content: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  seo: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  campaign: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  sales: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  referral: 'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300',
  ops: 'bg-slate-100 text-slate-700 dark:bg-slate-700/40 dark:text-slate-300',
  launch: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

const STATUS_DOTS: Record<string, string> = {
  planned: 'bg-slate-400',
  in_progress: 'bg-blue-500',
  completed: 'bg-emerald-500',
  blocked: 'bg-red-500',
};

const MODULE_ICON: Record<string, FC<{ className?: string }>> = {
  'blog-manager': FileEdit,
  'content-calendar': Calendar,
  campaigns: Megaphone,
  'content-factory': BookOpen,
  'sales-intelligence': Target,
  'referral-dashboard': UsersRound,
  'marketing-dashboard': Search,
};

const MODULE_ROUTES: Record<string, string> = {
  'blog-manager': '/growth/blog-manager',
  'content-calendar': '/growth/content-calendar',
  campaigns: '/growth/campaigns',
  'content-factory': '/growth/content-factory',
  'sales-intelligence': '/growth/sales-intelligence',
  'referral-dashboard': '/growth/referral-dashboard',
  'marketing-dashboard': '/growth/marketing-dashboard',
};

interface MilestoneCardProps {
  milestone: Milestone;
  onEdit: (m: Milestone) => void;
  onDelete: (id: string) => void;
}

const MilestoneCard: FC<MilestoneCardProps> = ({ milestone, onEdit, onDelete }) => {
  const navigate = useNavigate();
  const ModuleIcon = milestone.linkedModule ? MODULE_ICON[milestone.linkedModule] ?? Settings : null;
  const moduleRoute = milestone.linkedModule ? MODULE_ROUTES[milestone.linkedModule] : null;

  return (
    <div className="fc-card p-4 space-y-3 group">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${STATUS_DOTS[milestone.status] ?? 'bg-slate-400'}`} />
          <span className="font-medium text-sm text-slate-900 dark:text-white truncate">{milestone.title}</span>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          <button onClick={() => onEdit(milestone)} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onDelete(milestone.id)} className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/30 text-slate-400 hover:text-red-600 dark:hover:text-red-400">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Category badge */}
      <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${CATEGORY_COLORS[milestone.category] ?? CATEGORY_COLORS.ops}`}>
        {milestone.category}
      </span>

      {/* Meta row */}
      <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
        {milestone.dueDate && (
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {new Date(milestone.dueDate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
          </span>
        )}
        {milestone.assignee && (
          <span className="flex items-center gap-1">
            <User className="w-3 h-3" />
            {milestone.assignee}
          </span>
        )}
        {ModuleIcon && moduleRoute && (
          <Tooltip title={`Ouvrir ${milestone.linkedModule}`}>
            <button
              onClick={() => navigate(moduleRoute)}
              className="flex items-center gap-1 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
            >
              <Link2 className="w-3 h-3" />
              <ModuleIcon className="w-3 h-3" />
            </button>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default MilestoneCard;
